// data/events.js -- one stream of things that happen: launches, close approaches, meteor showers,
// storms that bring auroras, passes and trains over you, and solar and lunar eclipses.
//
// Contract (pure, no DOM, importable in node):
//   buildEvents(records, nowMs, opts)            -> [event], sorted by prominence, then time
//   nextEvent(type, fromMs, observer, records, {kind}) -> the first event of that type (and kind)
//                                                   after fromMs, or null
//   localCircumstances(event, observer)          -> a solar eclipse from one place, or null
//
// Spec 0031, 2026-09-23. registry/events.yaml has designed twelve event types since spec 0015, and
// until today no browser code read it: ui/next.js assembled seven kinds from whatever layers had
// loaded, and the two types a planetarium leads with -- eclipses, `source: computed` -- appeared
// nowhere, though the library that computes them had been vendored since the first commit. The
// list is now a consumer of this module, and spec 0030's `time: {event: solar-eclipse.next}` stops
// resolve through nextEvent().
//
// THE RECORD is spec 0015 design §1, with `t` in milliseconds because every caller compares it:
//   {id, type, t, t_precision, t_window, title, say, where, location_dependent, class, prominence,
//    source, links, record, detail}
// `record` is the loaded record the event is about (null for a shower, a storm or an eclipse), and
// `detail` carries the fields ui/next.js's rows already read, so moving the builders here changed
// no row (tests/test_events.mjs replays a twelve-row fixture against the pre-move output).
//
// `class` is this app's word for what the time IS: a launch's time is somebody's plan
// ('inferred'); an eclipse is computed to the minute from the Sun's and Moon's motion
// ('measured' in this app's sense: it is not anybody's plan and it will not slip).

import * as Astronomy from '../../vendor/astronomy.js';
import { EVENT_TYPES } from './events.registry.js';
import { SHOWERS } from './showers.js';
import { trainsFrom } from './trains.js';
import { predictPasses } from '../sky/passes.js';
import { peakInstant } from '../sky/radiants.js';
import { COPY, CITIES, t, fmt, timeText, UNITS } from '../copy/en.js';

const HOUR = 3600e3;
const DAY = 24 * HOUR;
const DEG = Math.PI / 180;

/** How far ahead the list looks for everything but eclipses (spec 0026 req 6). */
export const EVENT_HORIZON_MS = 30 * DAY;
/**
 * How far ahead eclipses are looked for, and how far nextEvent() looks for anything. Two to five
 * solar eclipses a year, most of them far away: thirty days would show one a year at best, and a
 * planetarium names the next one months ahead (spec 0031 req 5).
 */
export const ECLIPSE_HORIZON_MS = 400 * DAY;
/**
 * How far nextEvent() looks for an eclipse of one KIND (spec 0037, 2026-09-23). A kind is rarer than
 * the type: from today the next total lunar eclipse is 2028-12-31, 830 days off, and the longest
 * wait for a total solar eclipse this decade is 2028-07-22 to 2030-11-25, 856 days. 1 200 days
 * covers both with room. The search is still once a day (eclipseLists below): a median of 28 ms
 * for this window against 22 ms for 400 days, nine uncached calls each, node on this machine.
 */
export const ECLIPSE_KIND_HORIZON_MS = 1200 * DAY;
/** The kinds a `{event:, kind:}` reference may name, per type: the library's own words. */
export const ECLIPSE_KINDS = {
  'solar-eclipse': ['total', 'annular', 'partial', 'hybrid'],
  'lunar-eclipse': ['total', 'partial', 'penumbral'],
};

/** The turns of the year and the month each falls in (internal #384): a `kind:` a reference may name. */
export const SEASON_KINDS = {
  'solstice': ['june', 'december'],
  'equinox': ['march', 'september'],
};
/** Every type that has kinds, and the words a `{event:, kind:}` reference may use for each. */
export const EVENT_KINDS = { ...ECLIPSE_KINDS, ...SEASON_KINDS };

function startOfDay(ms) {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

const validObserver = (o) => !!o && Number.isFinite(o.latRad) && Number.isFinite(o.lonRad);

// ---------------------------------------------------------------------------------------
// Moved from ui/next.js on 2026-09-23 (spec 0031 task 2), unchanged. ui/next.js re-exports them,
// so tests/test_next.mjs and tests/test_radiants.mjs import them from where they always did.
// ---------------------------------------------------------------------------------------

/**
 * How much of the Moon is lit on the night of `dayMs` (at local 23:00), 0..1, or null. The line
 * registry/events.yaml asks a shower's card for is never the rate but the sky: a full Moon washes out
 * all but the brightest meteors, and it is the same fraction wherever the visitor stands.
 */
export function moonLitThatNight(dayMs) {
  try {
    const d = new Date(dayMs);
    d.setHours(23, 0, 0, 0);
    const f = Astronomy.Illumination(Astronomy.Body.Moon, d).phase_fraction;
    return Number.isFinite(f) ? f : null;
  } catch { return null; }
}

/**
 * Where a shower's radiant is on its peak night, for a place: the highest it gets between 20:00 and
 * 06:00 local, and the hour it gets there. Meteors appear only while the radiant is up, and more of
 * them the higher it is -- which is why a southern shower is a poor show from the north. Pure given
 * the place; null without one.
 */
export function radiantThatNight(shower, dayMs, observer) {
  if (!shower || !observer || !Number.isFinite(observer.latRad) || !Number.isFinite(observer.lonRad)) return null;
  const dec = Number(shower.dec) * DEG, ra = Number(shower.ra_h) * 15 * DEG;
  if (!Number.isFinite(dec) || !Number.isFinite(ra)) return null;
  let best = null;
  const start = new Date(dayMs); start.setHours(20, 0, 0, 0);
  for (let h = 0; h <= 10 * 4; h++) { // every quarter hour, 20:00 to 06:00
    const tMs = start.getTime() + h * 15 * 60e3;
    let lst;
    try { lst = Astronomy.SiderealTime(new Date(tMs)) * 15 * DEG + observer.lonRad; } catch { return null; }
    const ha = lst - ra;
    const alt = Math.asin(Math.sin(observer.latRad) * Math.sin(dec) + Math.cos(observer.latRad) * Math.cos(dec) * Math.cos(ha)) / DEG;
    if (!best || alt > best.altDeg) best = { altDeg: alt, tMs };
  }
  return best;
}

/**
 * Meteor-shower peaks inside the horizon, from registry/showers.yaml (via data/showers.js). A peak is
 * a calendar date that moves by about a day between years, so the row carries the date and says
 * "around", never a time. Today's peak still counts: tonight is when you would go out. Pure.
 */
export function showerItems(nowMs, horizonMs, showers, observer = null) {
  const out = [];
  const today = startOfDay(nowMs);
  for (const sh of Array.isArray(showers) ? showers : []) {
    const m = /^(\d{2})-(\d{2})$/.exec(String(sh && sh.peak || ''));
    if (!m) continue;
    const year = new Date(nowMs).getFullYear();
    for (const y of [year, year + 1]) {
      // The year's own date, from the Sun's longitude at the maximum (sky/radiants.js peakInstant):
      // the registry's `peak` is the IMO's date for one year and is a day off in another.
      const inst = peakInstant(sh, y);
      const day = inst !== null ? new Date(inst) : null;
      // The calendar's date is the maximum's date in UT, as the IMO prints it, read as the visitor's own.
      const at = day ? new Date(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), 12, 0, 0, 0).getTime() : new Date(y, Number(m[1]) - 1, Number(m[2]), 12, 0, 0, 0).getTime(); // local noon of the date
      if (startOfDay(at) < today) continue;
      if (at - nowMs < horizonMs) out.push({ kind: 'shower', record: null, label: sh.display, tMs: at, zhr: sh.zhr, showerId: sh.id, moonLit: moonLitThatNight(at), radiant: radiantThatNight(sh, at, observer) });
      break;
    }
  }
  return out;
}

/**
 * A geomagnetic storm, from NOAA's planetary Kp (the swpc-kp feed the space-weather line reads):
 * the storm under way now if the latest measured bin is Kp 5 or more, else the strongest forecast
 * bin of Kp 5 or more still ahead. One row, never three: NOAA forecasts in three-hour bins and a
 * storm spans several. registry/events.yaml's `aurora` event, which had no row anywhere. Pure.
 */
export function auroraItem(parsed, nowMs, horizonMs = 3 * DAY) {
  const rows = parsed && Array.isArray(parsed.forecast) ? parsed.forecast : [];
  const isMeasured = (r) => r.observed === 'observed' || r.observed === 'estimated';
  const measured = rows.filter(isMeasured);
  const latest = measured[measured.length - 1];
  if (latest && latest.kp >= 5 && nowMs - latest.tMs < 6 * HOUR) {
    return { kind: 'aurora', record: null, tMs: nowMs, kp: latest.kp, now: true };
  }
  let best = null;
  for (const r of rows) {
    if (isMeasured(r) || !(r.kp >= 5)) continue;
    if (r.tMs + 3 * HOUR <= nowMs || r.tMs - nowMs > horizonMs) continue;
    if (!best || r.kp > best.kp) best = r;
  }
  return best ? { kind: 'aurora', record: null, tMs: Math.max(best.tMs, nowMs), kp: best.kp, now: false } : null;
}

/** A launch's row, the first branch of the record walk ui/next.js had: the launches layer's NET. */
export function launchItem(r, nowMs, horizonMs) {
  if (!r || !r.meta) return null;
  const m = r.meta;
  if (r.layer === 'launches' && Number.isFinite(m.netMs) && m.netMs > nowMs && m.netMs - nowMs < horizonMs) {
    return { kind: 'launch', record: r, tMs: m.netMs, precision: m.netPrecision || null, status: m.statusAbbrev || null };
  }
  return null;
}

/**
 * How big a passing asteroid is, in metres, as the range its brightness allows: `{loM, hiM,
 * measured}`. A measured diameter is used when the catalogue has one. Otherwise it is worked out
 * from the absolute magnitude H for a surface that reflects between a quarter and a twentieth of
 * the light on it (the usual bounds for near-Earth asteroids): D = 1329 km / sqrt(albedo) x
 * 10^(-H/5). An estimate, and the row says so. Null with neither number.
 */
export function approachSize(m) {
  if (!m) return null;
  const d = Number(m.diameterKm);
  if (m.diameterKm !== null && m.diameterKm !== undefined && d > 0) return { loM: d * 1000, hiM: d * 1000, measured: true };
  const H = Number(m.absoluteMagnitude);
  if (m.absoluteMagnitude === null || m.absoluteMagnitude === undefined || !Number.isFinite(H)) return null;
  const base = 1329e3 * 10 ** (-H / 5);
  return { loM: base / Math.sqrt(0.25), hiM: base / Math.sqrt(0.05), measured: false };
}

/** A close approach's row: the walk's second branch, so a launch record never becomes one. */
export function approachItem(r, nowMs, horizonMs) {
  if (!r || !r.meta || launchItem(r, nowMs, horizonMs)) return null;
  const m = r.meta;
  if (Number.isFinite(m.closeApproachMs) && m.closeApproachMs > nowMs && m.closeApproachMs - nowMs < horizonMs) {
    // THE DISTANCE WAS NEVER SHOWN (public #313, found 2026-10-06): data/parsers.js
    // parseNeoApproaches writes `closeApproachLunarDistances` and `closeApproachDistanceKm`, and
    // this read `missDistanceLd` and `missDistanceKm`, which only the test's fixtures carried. So
    // every live row said "passes Earth" and never how close. Both spellings are read now.
    const firstNumber = (...vs) => { for (const v of vs) if (v !== null && v !== undefined && Number.isFinite(Number(v))) return Number(v); return null; };
    const km = firstNumber(m.missDistanceKm, m.closeApproachDistanceKm);
    const ld = firstNumber(m.missDistanceLd, m.closeApproachLunarDistances) ?? (km !== null ? km / UNITS.LUNAR_DISTANCE_KM : null);
    const item = { kind: 'approach', record: r, tMs: m.closeApproachMs, ld };
    const size = approachSize(m);
    if (size) item.size = size;
    return item;
  }
  return null;
}

// THE PASSES ARE WORKED OUT ONCE A QUARTER OF AN HOUR, NOT ONCE A MINUTE. ui/next.js rebuilds its list
// every 60 s, and with a place set that was a day of passes for some 180 satellites each time: MEASURED
// 2026-10-10 on an Iris Plus 640 laptop as one frame of 1.5 to 3.4 s a minute (the "three-second stall
// on the galaxy rung" of the roof trip was this, landing wherever the trip happened to be). A day of
// passes does not change in a minute, so the answer is kept for the same place, the same elements and a
// clock within PASS_MEMO_MS after the moment it was worked out for; a pass that has ended is left out.
export const PASS_MEMO_MS = 15 * 60e3;
let passMemo = null;
let passWork = 0;
/** How many times a day of passes has been worked out (tests/test_events.mjs counts, rather than times, the memo). */
export const passWorkCount = () => passWork;

// OFF THE MAIN THREAD (internal #562, 2026-10-10). Even kept for a quarter of an hour, the first working
// out after boot, and each one after a jumped clock or a new place, was one task of 1.5 to 3.6 s. A page
// that hands in a runner (ui/next.js: sky/passclient.js, the worker that already runs predictPasses for
// the Tonight view) gets the rows it HAS (an expired answer for the same place and elements, with the
// passes that ended left out; otherwise none) and is told through onPassesReady when the new answer is
// in. Nothing hands in a runner in node, so the tests and every other caller work as they did.
let passRunner = null;
let passPending = '';
const passListeners = new Set();
/** `run(message) -> Promise<{passes}>` (sky/passclient.js runPasses), or null to work them out in place again. */
export function usePassRunner(run) { passRunner = typeof run === 'function' ? run : null; passPending = ''; }
/** Call `fn` when a day of passes has been worked out off the main thread; returns the way to stop. */
export function onPassesReady(fn) { passListeners.add(fn); return () => passListeners.delete(fn); }

function askRunner(withOrbits, key, nowMs, observer) {
  if (passPending === key) return;
  passPending = key;
  const byId = new Map(withOrbits.map((r) => [r.id, r]));
  const msg = {
    fromMs: nowMs,
    hours: 24,
    observer: { latRad: observer.latRad, lonRad: observer.lonRad, latDeg: observer.latDeg, lonDeg: observer.lonDeg, altKm: observer.altKm || 0 },
    records: withOrbits.map((r) => ({ id: r.id, name: r.name, satrec: r.satrec, meta: {} })),
  };
  passWork += 1;
  new Promise((resolve) => resolve(passRunner(msg))).then((data) => {
    if (passPending === key) passPending = '';
    const rows = [];
    for (const p of (data && data.passes) || []) {
      const record = byId.get(p.recordId);
      if (!record || p.visible !== true) continue;
      const item = { kind: 'pass', record, tMs: p.startMs, peakEl: p.peakEl };
      rows.push({ item, endMs: Number.isFinite(p.endMs) ? p.endMs : p.startMs + 15 * 60e3 });
    }
    passMemo = { key, place: key.split('|').slice(0, 5).join('|'), atMs: nowMs, rows };
    for (const fn of [...passListeners]) { try { fn(); } catch { /* a listener is its own business */ } }
  }).catch(() => { if (passPending === key) passPending = ''; });
}

const placeKey = (o) => [o.latRad, o.latDeg, o.lonRad, o.lonDeg, o.altKm].join('|');
function passItems(records, nowMs, observer) {
  const out = [];
  if (!validObserver(observer)) return out;
  const withOrbits = (Array.isArray(records) ? records : []).filter((r) => r && r.satrec && (r.layer === 'stations' || r.layer === 'visual'));
  if (!withOrbits.length) return out;
  let epochs = 0;
  for (const r of withOrbits) epochs += Number(r.satrec.jdsatepoch) || 0;
  const key = [placeKey(observer), withOrbits.length, withOrbits[0].id, epochs].join('|');
  if (passMemo && passMemo.key === key && nowMs >= passMemo.atMs && nowMs - passMemo.atMs < PASS_MEMO_MS) {
    return passMemo.rows.filter((row) => row.endMs > nowMs).map((row) => ({ ...row.item }));
  }
  if (passRunner) {
    askRunner(withOrbits, key, nowMs, observer);
    // The last answer for the same PLACE is better than a blank while the new one is on its way. Not only for
    // the same elements (integration pass, 2026-10-10: a second catalogue landing ten seconds after the first
    // changed the key, and the three passes on the list went away for two seconds and came back): a day-old
    // prediction is off by seconds, and the row is replaced the moment the worker answers. Each row is handed
    // the record the catalogue holds NOW under its id; one that has left the catalogue has no row.
    const place = placeKey(observer);
    if (!passMemo || passMemo.place !== place || nowMs < passMemo.atMs || nowMs - passMemo.atMs >= 24 * 3600e3) return [];
    const now = new Map(withOrbits.map((r) => [r.id, r]));
    return passMemo.rows.filter((row) => row.endMs > nowMs && now.has(row.item.record.id)).map((row) => ({ ...row.item, record: now.get(row.item.record.id) }));
  }
  try {
    passWork += 1;
    const passes = predictPasses(withOrbits, observer, nowMs, 24).filter((p) => p.visible === true);
    const rows = [];
    for (const p of passes) {
      const item = { kind: 'pass', record: p.record, tMs: p.startMs, peakEl: p.peakEl };
      out.push(item);
      rows.push({ item: { ...item }, endMs: Number.isFinite(p.endMs) ? p.endMs : p.startMs + 15 * 60e3 });
    }
    passMemo = { key, place: key.split('|').slice(0, 5).join('|'), atMs: nowMs, rows };
  } catch { /* a pass we could not compute is a row we do not print */ }
  return out;
}

// Trains over you (spec 0026 req 17): the lead of each train that is still climbing, as one row.
function trainItems(records, nowMs, observer, stillRaisingBelowKm) {
  const out = [];
  if (!validObserver(observer)) return out;
  const trainRecords = (Array.isArray(records) ? records : []).filter((r) => r && r.satrec && r.layer === 'starlink-trains');
  for (const train of trainsFrom(trainRecords, nowMs, { stillRaisingBelowKm })) {
    if (train.stillRaising !== true || !train.lead || !train.lead.satrec) continue;
    try {
      const passes = predictPasses([train.lead], observer, nowMs, 24).filter((p) => p.visible === true);
      for (const p of passes.slice(0, 1)) out.push({ kind: 'train', record: train.lead, tMs: p.startMs, count: train.count });
    } catch { /* no row for a pass we could not compute */ }
  }
  return out;
}

// ---------------------------------------------------------------------------------------
// The station's next pass over one place, for a trip stop (spec 0038)
// ---------------------------------------------------------------------------------------

/** The station a `station-pass.next` stop counts to: the ISS, by its catalogue number. */
export const STATION_CATALOG = 25544;
/**
 * How far ahead a stop looks for the pass. Seven days, because elements are honest for about a
 * week (spec 0008) and the station crosses 10 degrees over any place below about 52 degrees of
 * latitude several times in that. The Next list keeps its own 24 hours (passItems above).
 */
export const STATION_PASS_HORIZON_MS = 7 * DAY;
/** A visible pass this soon wins over an earlier daytime one: two nights is worth waiting for. */
export const STATION_PASS_PREFER_VISIBLE_MS = 2 * DAY;

const isStation = (r) => {
  if (!r || !r.satrec || r.layer !== 'stations') return false;
  const n = r.meta ? (r.meta.noradId ?? r.meta.catalogueNumber) : null;
  return Number(n) === STATION_CATALOG;
};

// ONE SEARCH AN HOUR. Measured 2026-09-23 in node on this machine (tests/test_events.mjs prints it):
// seven days of one record is about 100 ms, and a trip asks twice -- when it is planned, for an
// honest count, and again at the stop -- with the picker planning it on every open. So the week's
// passes are kept for an hour, for the same record, elements and place, and filtered per call.
// One entry, never stored anywhere else: the observer in its key lives as long as the page.
let stationPassCache = { key: '', from: NaN, horizonMs: 0, passes: [] };
const STATION_PASS_REUSE_MS = HOUR;

function stationPasses(record, observer, nowMs, horizonMs) {
  const key = `${record.id}|${record.epoch}|${observer.latRad}|${observer.lonRad}|${observer.altKm || 0}`;
  const c = stationPassCache;
  if (c.key === key && nowMs >= c.from && nowMs - c.from < STATION_PASS_REUSE_MS && c.horizonMs >= horizonMs) {
    return c.passes.filter((p) => p.endMs > nowMs && p.startMs < nowMs + horizonMs);
  }
  let passes;
  try {
    // An hour of extra window so a reused list still reaches a full `horizonMs` past a later now.
    passes = predictPasses([record], observer, nowMs, (horizonMs + STATION_PASS_REUSE_MS) / HOUR);
  } catch { return []; }
  stationPassCache = { key, from: nowMs, horizonMs, passes };
  return passes.filter((p) => p.startMs < nowMs + horizonMs);
}

/**
 * THE STATION'S NEXT PASS OVER YOU, as an event: the peak of the first pass above 10 degrees in
 * seven days, preferring a pass you could SEE (sunlit, your sky dark) when one comes within two
 * days. Spec 0038 req 5, 2026-09-23.
 *
 * WHY NOT THE LIST'S OWN `station-pass` BUILDER. That one answers the Next list's question -- every
 * station and bright satellite you could see in the next 24 hours, timed at rise -- and a trip stop
 * asks a different one: the ISS, the next time it is over you at all, timed at the top of its arc,
 * because the camera is put there at that minute. At a latitude where the station only crosses in
 * daylight for a week the list shows nothing and the stop still has a pass, and the card says it
 * is a daytime one (`pass.visible`). The list's rows are unchanged.
 */
export function fromPasses(records, nowMs, { observer = null, passHorizonMs = STATION_PASS_HORIZON_MS, type = null } = {}) {
  if (!validObserver(observer) || !Number.isFinite(nowMs)) return [];
  const iss = (Array.isArray(records) ? records : []).filter(isStation);
  if (!iss.length) return [];
  const passes = stationPasses(iss[0], observer, nowMs, passHorizonMs);
  // A pass already under way when the window opens is clipped at the window's start, so its "peak"
  // is only the highest point of what is left, and one cut off by the window's end the same: the
  // stop is the NEXT whole crossing, so neither counts, and the peak must still be ahead.
  const ahead = passes.filter((p) => p.peakElDeg >= 10 && p.peakMs > nowMs && !p.clippedStart && !p.clippedEnd);
  const pick = ahead.find((p) => p.visible && p.peakMs < nowMs + STATION_PASS_PREFER_VISIBLE_MS) || ahead[0];
  if (!pick) return [];
  const ty = type || EVENT_TYPES.find((e) => e.id === 'station-pass') || { id: 'station-pass', display: 'station-pass', prominence: 2, source: 'celestrak-stations', locationDependent: true };
  return [{
    id: `station-pass:${pick.record.id}:${Math.round(pick.peakMs / 60e3)}`,
    type: 'station-pass',
    t: pick.peakMs,
    t_precision: 'minute',
    t_window: [pick.startMs, pick.endMs],
    title: ty.display,
    say: null,
    where: null,
    location_dependent: true,
    // A pass is propagated from elements somebody measured, so it is as good as their age: the
    // card prints that age as it does for every SGP4 record.
    class: 'inferred',
    prominence: ty.prominence,
    source: ty.source,
    links: [],
    record: pick.record,
    detail: { peakEl: pick.peakEl, visible: pick.visible },
    pass: pick,
  }];
}

// ---------------------------------------------------------------------------------------
// Items become records
// ---------------------------------------------------------------------------------------

/**
 * One of next.js's items as a spec 0015 record: the shared fields filled, the row's own fields
 * kept in `detail` so ui/next.js can turn it back into exactly the item it built before.
 */
function fromItem(type, item, cls, precision) {
  const { kind, record, tMs, ...detail } = item;
  const id = record && record.id != null ? `${type.id}:${record.id}` : `${type.id}:${detail.showerId || ''}:${Math.round(tMs / 60e3)}`;
  return {
    id,
    type: type.id,
    t: tMs,
    t_precision: precision,
    t_window: [tMs, tMs],
    title: item.label || (record && record.name) || type.display,
    say: null, // the sentence is ui/next.js rowText()'s, from `detail`, as it was before the move
    where: null,
    location_dependent: type.locationDependent,
    class: cls,
    prominence: type.prominence,
    source: type.source,
    links: [],
    record: record || null,
    detail,
  };
}

// NET precision from Launch Library ("Minute", "Hour", "Day", "Month", ...), lower-cased; a launch
// with none is a plan to the minute that may still move, which `class: 'inferred'` already says.
const launchPrecision = (it) => (it.precision ? String(it.precision).toLowerCase() : 'minute');

// ---------------------------------------------------------------------------------------
// Eclipses: computed here with astronomy-engine 2.1.19 (site/vendor/astronomy.js, MIT)
// ---------------------------------------------------------------------------------------

const EARTH_RADIUS_KM = 6371;

/** Great-circle kilometres between two points in degrees. */
function kmBetween(lat1, lon1, lat2, lon2) {
  const p1 = lat1 * DEG, p2 = lat2 * DEG, dl = (lon2 - lon1) * DEG;
  const c = Math.sin(p1) * Math.sin(p2) + Math.cos(p1) * Math.cos(p2) * Math.cos(dl);
  return EARTH_RADIUS_KM * Math.acos(Math.max(-1, Math.min(1, c)));
}

/**
 * The bundled city nearest a point, and how far away it is. The spec named ui/controls.js
 * findCity() as "the existing nearest-city lookup"; it is a search by name, and nothing did this.
 * A place word for where an eclipse is deepest is the only caller: the point is often at sea, so
 * the row says the distance rather than pretending the shadow crosses the city.
 */
export function nearestCity(latDeg, lonDeg, cities = CITIES) {
  let best = null;
  for (const c of Array.isArray(cities) ? cities : []) {
    if (!c || !Number.isFinite(c.latDeg) || !Number.isFinite(c.lonDeg)) continue;
    const km = kmBetween(latDeg, lonDeg, c.latDeg, c.lonDeg);
    if (!best || km < best.km) best = { city: c, km };
  }
  return best;
}

/** Within this, "near Cairo"; beyond it, "about 600 km from Cairo". */
const NEAR_KM = 250;

/** "near Cairo" or "about 900 km from Sao Paulo", for the point where a solar eclipse is deepest. */
export function whereWords(latDeg, lonDeg, cities = CITIES) {
  const T = COPY.nextList;
  const n = Number.isFinite(latDeg) && Number.isFinite(lonDeg) ? nearestCity(latDeg, lonDeg, cities) : null;
  if (!n) return null;
  if (n.km < NEAR_KM) return t(T.eclipseNear, { city: n.city.name });
  return t(T.eclipseFrom, { km: fmt.int(Math.round(n.km / 100) * 100), city: n.city.name });
}

const kindWord = (kind) => (COPY.nextList.eclipseKinds || {})[kind] || kind;

/** The record's own sentence (spec 0015's `say`); the list row adds local times to it. */
export function eclipseSentence(ev, dateText) {
  const T = COPY.nextList;
  const kind = kindWord(ev.kind);
  if (ev.type === 'solar-eclipse') {
    return ev.where && ev.where.words
      ? t(T.solarEclipse, { kind, date: dateText, where: ev.where.words })
      : t(T.solarEclipseGrazing, { kind, date: dateText });
  }
  return ev.kind === 'penumbral'
    ? t(T.lunarEclipsePenumbral, { kind, date: dateText })
    : t(T.lunarEclipse, { kind, date: dateText });
}

function eclipseRecord(type, kind, peakMs, window, extra) {
  const ev = {
    id: `${type.id}:${new Date(peakMs).toISOString().slice(0, 10)}`,
    type: type.id,
    t: peakMs,
    t_precision: 'minute',
    t_window: window,
    title: t(type.id === 'solar-eclipse' ? COPY.nextList.solarEclipseTitle : COPY.nextList.lunarEclipseTitle, { kind: kindWord(kind) }),
    say: null,
    where: null,
    location_dependent: type.locationDependent,
    class: 'measured',
    prominence: type.prominence,
    source: 'computed',
    links: [],
    record: null,
    kind,
    obscuration: null,
    local: null,
    detail: {},
    ...extra,
  };
  ev.say = eclipseSentence(ev, timeText.longDate(peakMs));
  return ev;
}

// ONE SEARCH A DAY. Measured 2026-09-23 in node on this machine (tests/test_events.mjs prints it):
// both searches over a 400-day window take a median of 14.5 ms, inside the spec's 20 ms, and the
// first call about twice that. The spec says memoise only past 50 ms; this does it anyway, because
// the Next list rebuilds every minute and on every layer landing, a phone is several times slower
// than this machine, and the answer only changes when the window's start passes an eclipse. So the
// lists are searched once per UTC day, a day either side, and filtered per call (0.1 ms). One
// entry, never stored, and no observer in it: nothing about the visitor is kept.
let eclipseCache = { day: NaN, horizonMs: 0, solar: [], lunar: [] };

function eclipseLists(nowMs, horizonMs) {
  const day = Math.floor(nowMs / DAY);
  if (eclipseCache.day === day && eclipseCache.horizonMs >= horizonMs) return eclipseCache;
  const from = new Date((day - 1) * DAY);
  const until = (day + 1) * DAY + horizonMs;
  const solar = [];
  const lunar = [];
  try {
    let ecl = Astronomy.SearchGlobalSolarEclipse(from);
    while (ecl && ecl.peak.date.getTime() < until) {
      solar.push(ecl);
      ecl = Astronomy.NextGlobalSolarEclipse(ecl.peak);
    }
  } catch { /* an eclipse we could not compute is a row we do not print */ }
  try {
    let ecl = Astronomy.SearchLunarEclipse(from);
    while (ecl && ecl.peak.date.getTime() < until) {
      lunar.push(ecl);
      ecl = Astronomy.NextLunarEclipse(ecl.peak);
    }
  } catch { /* likewise */ }
  eclipseCache = { day, horizonMs, solar, lunar };
  return eclipseCache;
}

function solarEclipses(records, nowMs, { eclipseHorizonMs, observer, type }) {
  const out = [];
  for (const ecl of eclipseLists(nowMs, eclipseHorizonMs).solar) {
    const peakMs = ecl.peak.date.getTime();
    if (peakMs <= nowMs || peakMs - nowMs >= eclipseHorizonMs) continue;
    // latitude/longitude are the centre of the shadow at peak, given for total and annular
    // eclipses only (the library's GlobalSolarEclipseInfo); a partial one has no such point.
    const hasPoint = Number.isFinite(ecl.latitude) && Number.isFinite(ecl.longitude);
    const ev = eclipseRecord(type, ecl.kind, peakMs, [peakMs, peakMs], {
      where: hasPoint ? { kind: 'point', lat: ecl.latitude, lon: ecl.longitude, words: whereWords(ecl.latitude, ecl.longitude) } : null,
      obscuration: Number.isFinite(ecl.obscuration) ? ecl.obscuration : null,
    });
    if (validObserver(observer)) ev.local = localCircumstances(ev, observer);
    out.push(ev);
  }
  return out;
}

function lunarEclipses(records, nowMs, { eclipseHorizonMs, type }) {
  const out = [];
  for (const ecl of eclipseLists(nowMs, eclipseHorizonMs).lunar) {
    const peakMs = ecl.peak.date.getTime();
    // The window is the partial (umbral) phase where there is one, else the penumbral: semi-
    // durations in minutes either side of the peak (LunarEclipseInfo.sd_partial / sd_penum).
    const sd = ecl.sd_partial > 0 ? ecl.sd_partial : ecl.sd_penum;
    const window = Number.isFinite(sd) ? [peakMs - sd * 60e3, peakMs + sd * 60e3] : [peakMs, peakMs];
    if (peakMs <= nowMs || peakMs - nowMs >= eclipseHorizonMs) continue;
    out.push(eclipseRecord(type, ecl.kind, peakMs, window, { obscuration: Number.isFinite(ecl.obscuration) ? ecl.obscuration : null }));
  }
  return out;
}

/**
 * A solar eclipse from one place (spec 0031 req 6): when it begins, peaks and ends there, and how
 * much of the Sun is covered; or `{visible: false}` with the reason. Null for anything but a solar
 * eclipse, or without a place. Computed on demand and never stored, because the observer is never
 * persisted (main.js) and this keeps that.
 *
 * Field names are the library's LocalSolarEclipseInfo (astronomy.js, read 2026-09-23):
 * partial_begin / peak / partial_end are EclipseEvents with `.time` (AstroTime) and `.altitude`
 * (degrees, the Sun's, refraction-corrected); `obscuration` is the fraction of the Sun's disc.
 */
export function localCircumstances(ev, observer) {
  if (!ev || ev.type !== 'solar-eclipse' || !observer) return null;
  const latDeg = Number.isFinite(observer.latDeg) ? observer.latDeg : Number.isFinite(observer.latRad) ? observer.latRad / DEG : NaN;
  const lonDeg = Number.isFinite(observer.lonDeg) ? observer.lonDeg : Number.isFinite(observer.lonRad) ? observer.lonRad / DEG : NaN;
  if (!Number.isFinite(latDeg) || !Number.isFinite(lonDeg)) return null;
  let local;
  try {
    const obs = new Astronomy.Observer(latDeg, lonDeg, (Number(observer.altKm) || 0) * 1000);
    local = Astronomy.SearchLocalSolarEclipse(new Date(ev.t - 2 * DAY), obs);
  } catch { return null; }
  // The search returns the next eclipse seen ANYWHERE the Moon's shadow touches this place; if that
  // is a different eclipse, this one misses it.
  if (!local || Math.abs(local.peak.time.date.getTime() - ev.t) > DAY) return { visible: false, reason: 'outside' };
  if (!(local.peak.altitude > 0)) return { visible: false, reason: 'below-horizon' };
  return {
    visible: true,
    kind: local.kind,
    beginMs: local.partial_begin.time.date.getTime(),
    peakMs: local.peak.time.date.getTime(),
    endMs: local.partial_end.time.date.getTime(),
    obscuration: local.obscuration,
    // A contact with the Sun below the horizon happens out of sight; the row says so.
    beginsBelow: local.partial_begin.altitude < 0,
    endsBelow: local.partial_end.altitude < 0,
  };
}

// ---------------------------------------------------------------------------------------
// Solstices and equinoxes (internal #384)
// ---------------------------------------------------------------------------------------

const SEASON_FIELDS = { march: 'mar_equinox', june: 'jun_solstice', september: 'sep_equinox', december: 'dec_solstice' };

/**
 * The solstices (`type.id` 'solstice') or equinoxes ('equinox') after `nowMs`, inside `horizonMs`:
 * the instants the Sun's apparent ecliptic longitude is 90 and 270 degrees, or 0 and 180
 * (astronomy-engine Seasons(year), good to well under a minute for these centuries; tests/
 * test_events.mjs holds 2026 to 2030 to the published table). `kind` is the month it falls in.
 * The same instant everywhere, so nothing about the visitor goes in. A year is four numbers and
 * about a millisecond: not cached.
 */
function seasons(records, nowMs, { horizonMs, type }) {
  const out = [];
  const kinds = SEASON_KINDS[type.id] || [];
  const T = COPY.nextList;
  const y0 = new Date(nowMs).getUTCFullYear();
  const y1 = new Date(nowMs + horizonMs).getUTCFullYear();
  for (let y = y0; y <= y1; y += 1) {
    let s = null;
    try { s = Astronomy.Seasons(y); } catch { s = null; }
    if (!s) continue;
    for (const kind of kinds) {
      const at = s[SEASON_FIELDS[kind]];
      const tMs = at && at.date ? at.date.getTime() : NaN;
      if (!(tMs > nowMs) || tMs - nowMs >= horizonMs) continue;
      out.push({
        id: `${type.id}:${new Date(tMs).toISOString().slice(0, 10)}`,
        type: type.id,
        t: tMs,
        t_precision: 'minute',
        t_window: [tMs, tMs],
        title: T.seasonTitles[kind],
        say: t(T.seasons[kind], { date: timeText.longDate(tMs) }),
        where: null,
        location_dependent: type.locationDependent,
        class: 'measured',
        prominence: type.prominence,
        source: 'computed',
        links: [],
        record: null,
        kind,
        detail: {},
      });
    }
  }
  return out.sort((a, b) => a.t - b.t);
}

// ---------------------------------------------------------------------------------------
// The stream
// ---------------------------------------------------------------------------------------

const BUILDERS = {
  'launch': (records, nowMs, o) => (Array.isArray(records) ? records : [])
    .map((r) => launchItem(r, nowMs, o.horizonMs)).filter(Boolean)
    .map((it) => fromItem(o.type, it, 'inferred', launchPrecision(it))),
  'close-approach': (records, nowMs, o) => (Array.isArray(records) ? records : [])
    .map((r) => approachItem(r, nowMs, o.horizonMs)).filter(Boolean)
    .map((it) => fromItem(o.type, it, 'measured', 'minute')),
  'meteor-shower': (records, nowMs, o) => (o.showers ? showerItems(nowMs, o.horizonMs, o.showers, o.observer) : [])
    .map((it) => fromItem(o.type, it, 'inferred', 'day')),
  'station-pass': (records, nowMs, o) => passItems(records, nowMs, o.observer)
    .map((it) => fromItem(o.type, it, 'inferred', 'minute')),
  'starlink-train': (records, nowMs, o) => trainItems(records, nowMs, o.observer, o.trainThresholdKm)
    .map((it) => fromItem(o.type, it, 'inferred', 'minute')),
  'aurora': (records, nowMs, o) => {
    const a = o.spaceWeather ? auroraItem(o.spaceWeather, nowMs) : null;
    return a ? [fromItem(o.type, a, a.now ? 'measured' : 'inferred', 'hour')] : [];
  },
  'solar-eclipse': solarEclipses,
  'lunar-eclipse': lunarEclipses,
  'solstice': seasons,
  'equinox': seasons,
};

/** The types worked out here with no data at all (scripts/check_registry.py COMPUTED_EVENT_TYPES). */
const COMPUTED = new Set(['solar-eclipse', 'lunar-eclipse', 'solstice', 'equinox']);

/** The types the browser builds: enabled in the registry and with a builder here. */
export const BUILT_TYPES = EVENT_TYPES.filter((ty) => ty.enabled && BUILDERS[ty.id]).map((ty) => ty.id);

/**
 * Every event from what is loaded and what can be computed. `records` is everything loaded;
 * options: `observer` ({latRad, lonRad, latDeg?, lonDeg?, altKm}) adds passes, trains and local
 * eclipse circumstances; `horizonMs` (30 days) bounds all but eclipses; `eclipseHorizonMs` (400
 * days) bounds those; `showers` (registry/showers.yaml by default, null for none) and
 * `spaceWeather` (a parsed swpc-kp reading) feed their types; `eclipses: false` leaves the computed
 * types out, for a caller asking only about what it has loaded. Only future events. Pure but for the
 * one-a-day eclipse search above.
 */
export function buildEvents(records, nowMs, {
  observer = null,
  horizonMs = EVENT_HORIZON_MS,
  eclipseHorizonMs = ECLIPSE_HORIZON_MS,
  showers = SHOWERS,
  spaceWeather = null,
  trainThresholdKm,
  eclipses = true,
} = {}) {
  const out = [];
  if (!Number.isFinite(nowMs)) return out;
  const obs = validObserver(observer) ? observer : null;
  for (const type of EVENT_TYPES) {
    if (!type.enabled || !BUILDERS[type.id]) continue;
    if (!eclipses && COMPUTED.has(type.id)) continue;
    out.push(...BUILDERS[type.id](records, nowMs, { observer: obs, horizonMs, eclipseHorizonMs, showers, spaceWeather, trainThresholdKm, type }));
  }
  return out.sort((a, b) => (a.prominence - b.prominence) || (a.t - b.t));
}

/**
 * The first event of `type` after `fromMs`, within 400 days, or null: the one call spec 0030's
 * resolveStopTime() makes for a `time: {event: <type>.next}` stop. Nothing else in the trip machine
 * knows about events. `records` is only needed for types built from loaded records (a
 * `station-pass` needs the stations layer, `ctx.recordsFor('stations')`); an eclipse needs nothing.
 * An unknown or disabled type is null, never a throw.
 *
 * `kind` (spec 0037) narrows an eclipse to one of ECLIPSE_KINDS -- `{event: solar-eclipse.next,
 * kind: total}` -- and looks 1 200 days ahead instead of 400, because a kind is rarer than its
 * type. A kind the type does not have is null, the same as an event nobody can find.
 */
export function nextEvent(type, fromMs, observer = null, records = [], { kind = null } = {}) {
  const ty = EVENT_TYPES.find((x) => x.id === type);
  if (!ty || !ty.enabled || !BUILDERS[type] || !Number.isFinite(fromMs)) return null;
  if (kind !== null && kind !== undefined && !(EVENT_KINDS[type] || []).includes(kind)) return null;
  const obs = validObserver(observer) ? observer : null;
  // A stop's `station-pass.next` is the ISS over this place within a week (fromPasses, spec 0038),
  // not the Next list's 24 hours of anything visible.
  if (type === 'station-pass') return fromPasses(records, fromMs, { observer: obs, type: ty })[0] || null;
  // A kind of eclipse is rarer than its type; a turn of the year comes round every year.
  const horizon = kind && ECLIPSE_KINDS[type] ? ECLIPSE_KIND_HORIZON_MS : ECLIPSE_HORIZON_MS;
  const found = BUILDERS[type](records, fromMs, {
    observer: obs, horizonMs: horizon, eclipseHorizonMs: horizon, showers: SHOWERS, spaceWeather: null, type: ty,
  }).filter((e) => e.t > fromMs && (!kind || e.kind === kind)).sort((a, b) => a.t - b.t);
  return found[0] || null;
}
